'use client';

import React from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { notify } from 'src/utils/notifications';
import Paginator from './Paginator';
import TableV2 from './Tables/TableV2';
import TableStateWrapper, { TableStateProps } from './TableStateWrapper';

export type PaginatedTableProps = {
	tableGrid: string;
	pageSize: number;
	currentPage: number;
	totalItems: number;
	tableContentTop: React.ReactNode;
	tableContentMiddle: React.ReactNode;
	pageChangeCallback: (newPage: number) => void;
	hideHeadersOnMobile?: boolean;
	tableContentBottom?: React.ReactNode;
	noBorder?: boolean;
	innerClassName?: string;
	className?: string;
	paginatorClassName?: string;
	fillSpace?: boolean;
	tableState: TableStateProps;
	setIsScrollable?: (isScrollable: boolean) => void;
	notRounded?: boolean;
	fixedMaxPageIndex?: number;
	/**
	 * Token-based pagination allows the API server to keep track of where to continue fetching the next set of records.
	 * This means it can only go 1 page forward max, and can go backwards to any page that has been fetched (and token stored).
	 */
	isTokenBasedPagination?: boolean;
	disableRateLimitNotification?: boolean;
	// New props for items per page functionality
	pageSizeOptions?: number[];
	onPageSizeChange?: (newPageSize: number) => void;
	showItemsPerPage?: boolean;
	// New prop to show both items per page and tableContentBottom
	showBothLeftContent?: boolean;
	// New prop for right content area
	tableContentRight?: React.ReactNode;
	// Hide the double arrow button (last page button)
	hideLastPageButton?: boolean;
	// Number of page options to show for token-based pagination
	tokenBasedPaginationPageCount?: number;
	// Force showing pagination even with single page
	forceShowPagination?: boolean;
};

const PaginatedTableV2 = (props: PaginatedTableProps) => {
	const debouncedExecutePageChange = useDebouncedCallback(
		(newPage: number) => {
			props.pageChangeCallback(newPage);
		},
		500,
		{ leading: true }
	);

	const handlePageChange = (newPage: number) => {
		if (
			debouncedExecutePageChange.isPending() &&
			!props.disableRateLimitNotification
		) {
			notify({
				id: 'paginationRateLimit',
				type: 'info',
				message: 'Too many requests',
				description:
					"You can jump directly to a history page by clicking the '...' button",
			});
		}
		debouncedExecutePageChange(newPage);
	};

	const paginator = (props.totalItems > props.pageSize ||
		props.forceShowPagination) && (
		<Paginator
			pageSize={props.pageSize}
			pageIndex={props.currentPage}
			onPageChange={handlePageChange}
			totalCount={props.totalItems}
			message={props.tableContentBottom ?? null}
			fixedMaxPageIndex={props.fixedMaxPageIndex}
			isTokenBasedPagination={props.isTokenBasedPagination}
			disableRateLimitNotification={props.disableRateLimitNotification}
			pageSizeOptions={props.pageSizeOptions}
			onPageSizeChange={props.onPageSizeChange}
			showItemsPerPage={props.showItemsPerPage}
			showBothLeftContent={props.showBothLeftContent}
			hideLastPageButton={props.hideLastPageButton}
			tokenBasedPaginationPageCount={props.tokenBasedPaginationPageCount}
			noBorder={props.noBorder}
			className={props.paginatorClassName}
		/>
	);

	return (
		<TableStateWrapper {...props.tableState} paginator={paginator}>
			<TableV2.Skeleton
				top={props.tableContentTop}
				middle={props.tableContentMiddle}
				hideHeadersOnMobile={props.hideHeadersOnMobile}
				noBorder={props.noBorder}
				innerClassName={props.innerClassName}
				className={props.className}
				fillSpace={props.fillSpace}
				notRounded={props.notRounded}
				setIsScrollable={props.setIsScrollable}
			/>
		</TableStateWrapper>
	);
};

export default PaginatedTableV2;
